/**
 * usePatientInsurances.js
 * Carteirinhas de convênio de uma pessoa e a última cobertura usada em
 * agendamento (patients/insurance/list.php via DataProvider.GetPatientInsurances).
 *
 * Usado pelo bloco "Cobertura" do drawer de agendamento e pelo diálogo de
 * cobertura do painel de detalhe. Recarrega quando a pessoa muda; `addCard`
 * coloca na lista uma carteirinha cadastrada na hora, sem nova ida ao servidor.
 */

import { useCallback, useEffect, useState } from 'react';
import { DataProviderInstance } from '../../../../../api/DataProvider';

const EMPTY = { cards: [], lastCoverage: null };

export default function usePatientInsurances(patientId, enabled = true) {
  const { GetPatientInsurances } = DataProviderInstance();
  const [data, setData] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  // Muda a cada resposta: quem usa o hook aplica o padrão inteligente uma vez por carga
  const [loadedKey, setLoadedKey] = useState(null);

  useEffect(() => {
    if (!enabled || !patientId) {
      setData(EMPTY);
      setLoadedKey(null);
      return undefined;
    }
    let active = true;
    setLoading(true);
    GetPatientInsurances(patientId, (result) => {
      if (!active) return;
      setLoading(false);
      const payload = result?.status === 'SUCCESS' ? (result.data || {}) : {};
      setData({
        cards: Array.isArray(payload.insurances) ? payload.insurances : [],
        lastCoverage: payload.last_coverage || null,
      });
      setLoadedKey(`${patientId}:${Date.now()}`);
    });
    return () => { active = false; };
  }, [patientId, enabled]); // eslint-disable-line react-hooks/exhaustive-deps

  const addCard = useCallback((card) => {
    if (!card) return;
    setData(prev => ({
      ...prev,
      cards: [{ active: 1, expired: false, ...card }, ...prev.cards.filter(c => String(c.id) !== String(card.id))],
    }));
  }, []);

  const activeCards = data.cards.filter(c => Number(c.active ?? 1) === 1);

  return {
    cards: data.cards,
    activeCards,
    lastCoverage: data.lastCoverage,
    loading,
    loadedKey,
    addCard,
  };
}
